// components/plScenario/ViewSettingsDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDownIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const ViewSettingsDropdown = ({ 
  // View settings
  viewMode,
  detailLevel,
  onViewModeChange,
  hideZeroRows,
  onToggleHideZeroRows,
  
  // Visibility settings
  hiddenCount,
  isVisibilityEditMode,
  showHiddenItems,
  onStartVisibilityEditing,
  onToggleShowHidden,
  onManageHidden,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleViewModeSelect = (mode, detail) => {
    onViewModeChange(`${mode}-${detail}`);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Cog6ToothIcon className="w-4 h-4" />
        <span>View Options</span>
        {hiddenCount > 0 && (
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
            {hiddenCount}
          </span>
        )}
        <ChevronDownIcon 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-300 rounded-lg shadow-xl z-50 overflow-hidden">
          
          {/* TIME PERIOD */}
          <div className="py-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Time Period
            </div>
            <button
              onClick={() => handleViewModeSelect('monthly', detailLevel)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>Show by Month</span>
              {viewMode === 'monthly' && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>
            <button
              onClick={() => handleViewModeSelect('quarterly', detailLevel)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>Show by Quarter</span>
              {viewMode === 'quarterly' && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>
          </div>

          <div className="border-t border-gray-200" />

          {/* DETAIL LEVEL */}
          <div className="py-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Detail Level
            </div>
            <button
              onClick={() => handleViewModeSelect(viewMode, 'expanded')}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>All Line Items</span>
              {detailLevel === 'expanded' && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>
            <button
              onClick={() => handleViewModeSelect(viewMode, 'categories')}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>Categories Only</span>
              {detailLevel === 'categories' && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>
            <button
              onClick={() => handleViewModeSelect(viewMode, 'sections')}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>Sections Only</span>
              {detailLevel === 'sections' && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>
          </div>

          <div className="border-t border-gray-200" />

          {/* VISIBILITY OPTIONS */}
          <div className="py-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Visibility
            </div>
            
            {/* Hide Zero Rows Toggle */}
            <button
              onClick={() => {
                onToggleHideZeroRows();
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                <span>Hide Zero Rows</span>
              </div>
              {hideZeroRows && (
                <CheckIcon className="w-4 h-4 text-blue-600" />
              )}
            </button>

            {/* Edit Visibility */}
            <button
              onClick={() => {
                onStartVisibilityEditing();
                setIsOpen(false);
              }}
              disabled={isVisibilityEditMode}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <EyeIcon className="w-4 h-4 text-gray-500" />
                <span>Edit Line Visibility</span>
              </div>
              {hiddenCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                  {hiddenCount}
                </span>
              )}
            </button>

            {/* Show Hidden Items Toggle */}
            {hiddenCount > 0 && (
              <button
                onClick={() => {
                  onToggleShowHidden();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  {showHiddenItems ? (
                    <EyeIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                  )}
                  <span>Show Hidden Items</span>
                </div>
                {showHiddenItems && (
                  <CheckIcon className="w-4 h-4 text-blue-600" />
                )}
              </button>
            )}

            {/* Manage Hidden Items */}
            {hiddenCount > 0 && (
              <button
                onClick={() => {
                  onManageHidden();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors font-medium"
              >
                Manage Hidden Items ({hiddenCount})
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSettingsDropdown;